import axios from 'axios';
import { computed, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/composables/useToast';
import { visitAfterSave } from '@/utils/formHelpers';
import HeraMaintenanceNewSchedaExtension from './components/HeraMaintenanceNewSchedaExtension.vue';
import { MAINTENANCE_PHOTO_LIMIT } from './offline/maintenancePhotos';
import {
    getMaintenanceReferenceData,
    maintenanceDraftKey,
    putMaintenanceReferenceData,
} from './offline/maintenanceDb';
import { useMaintenanceOfflineForm } from './offline/useMaintenanceOfflineForm';

const SHEETS = new Set(['maintenance_degradation', 'maintenance_intervention']);
const truthy = value => value === true || value === 'true' || value === 1 || value === '1';

const scalarId = value => {
    if (Array.isArray(value)) return scalarId(value[0]);
    if (value && typeof value === 'object') return scalarId(value.id ?? value.value);
    return value === null || value === undefined || value === '' ? null : String(value);
};

const multipleIds = value => (Array.isArray(value) ? value : [value])
    .map(scalarId)
    .filter(Boolean)
    .filter((id, index, source) => source.indexOf(id) === index);

export function normalizeMaintenancePhotoTypes(payload) {
    const options = payload?.options ?? payload ?? [];
    if (!Array.isArray(options)) return [];

    return options
        .map(option => ({
            value: option?.id ?? option?.value ?? null,
            label: option?.name ?? option?.label ?? '',
        }))
        .filter(option => option.value !== null && option.label !== '')
        .map(option => ({ ...option, value: String(option.value) }));
}

export function configureOfflinePhotoTypeField(photoList, { scope, sheetName }) {
    const field = photoList?.element?.schema?.photoTypeId;
    const fieldId = field?.fieldId ?? field?.id;
    const endpoint = field?.optionsEndpoint
        ?? (fieldId ? `/api/form-field/${fieldId}/options` : null);
    if (!field || !fieldId || !endpoint) return null;

    const cacheKey = `photo-types:${sheetName}:${fieldId}`;
    let optionsPromise = null;
    field.items = () => {
        if (optionsPromise) return optionsPromise;

        optionsPromise = (async () => {
            const cached = await getMaintenanceReferenceData(scope, cacheKey).catch(() => null);
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                return normalizeMaintenancePhotoTypes(cached?.payload);
            }

            try {
                const response = await axios.get(endpoint);
                await putMaintenanceReferenceData(scope, cacheKey, response.data).catch(() => undefined);
                return normalizeMaintenancePhotoTypes(response.data);
            } catch (error) {
                if (!cached?.payload) {
                    console.warn('Unable to load maintenance photo types', error);
                }
                return normalizeMaintenancePhotoTypes(cached?.payload);
            }
        })();

        return optionsPromise;
    };

    return field.items;
}

export function buildMaintenanceCreateAgainUrl(sheetName, model = {}) {
    const params = new URLSearchParams();
    const parkId = scalarId(model.parkId);
    const projectId = scalarId(model.projectId);
    if (parkId) params.set('prefill_park_id', parkId);
    if (projectId) params.set('prefill_project_id', projectId);

    if (sheetName === 'maintenance_degradation') {
        const elementId = scalarId(model.topographicElement3Id);
        if (elementId) params.set('prefill_topographic_element_3_id', elementId);
    } else {
        const elementIds = multipleIds(model.topographic_elements_3);
        if (elementIds.length) params.set('prefill_topographic_element_3_ids', elementIds.join(','));
    }

    const query = params.toString();
    return `/database/nuova-scheda/${sheetName}${query ? `?${query}` : ''}`;
}

export function resolveMaintenanceSaveRedirect(sheetName, saveAction, model = {}) {
    if (saveAction === 'new') return buildMaintenanceCreateAgainUrl(sheetName, model);
    if (saveAction === 'close') return '/manutenzione';
    return null;
}

export function resolveMaintenanceSaveAction(lastAction, submitOptions = {}, submitAction = null) {
    return lastAction ?? submitOptions?.saveAction ?? submitAction?.value ?? 'continue';
}

function createHeraMaintenanceNewSchedaExtension(context) {
    const {
        props,
        editing,
        tenantContext,
        page,
        data,
        schema,
        isSubmitting,
        lastSaveAction,
    } = context;
    if (!SHEETS.has(props.sheet.name)) return null;

    const toast = useToast();
    const offlineMode = !editing && truthy(tenantContext.settings['feature.offline_field_ops']);
    const clientUuid = ref(uuidv4());
    const draftKey = maintenanceDraftKey({ sheetName: props.sheet.name });
    const scope = {
        tenantId: tenantContext.id,
        userId: page.props?.auth?.user?.id ?? tenantContext.user?.id,
    };
    const offline = offlineMode ? useMaintenanceOfflineForm({
        scope,
        draftKey,
        sheetName: props.sheet.name,
        endpoint: `/api/${props.sheet.name}`,
    }) : null;
    const initialized = ref(false);
    const showCompletion = computed(() => editing
        && props.sheet.name === 'maintenance_intervention'
        && props.accessCapabilities?.can_validate === true);
    const completionOfflineEnabled = computed(() => truthy(
        tenantContext.settings['feature.offline_field_ops'],
    ));
    const statusLabel = computed(() => ({
        idle: 'Nessuna bozza locale',
        draft: 'Bozza salvata sul dispositivo',
        pending: 'In attesa di sincronizzazione',
        uploading: 'Invio fotografie…',
        submitting: 'Salvataggio scheda…',
        'validation-error': 'Correggi i campi indicati e riprova',
        conflict: 'Conflitto da verificare',
        failed: 'Sincronizzazione non riuscita',
        synced: 'Sincronizzata',
    }[offline?.status.value] ?? 'Bozza locale'));
    let draftTimer = null;
    let photoTypeItemsLoader = null;

    const transformSchema = () => {
        if (schema.value?.actions?.schema) {
            // Size actions against the available form width (including its sidebar),
            // rather than squeezing long labels into one unbroken row on tablets.
            schema.value.actions.addClass = {
                ...schema.value.actions.addClass,
                wrapper: 'grid grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] mt-8 mb-16 gap-3 '
                    + '[&>div]:!col-span-1 [&>div]:min-w-0 [&_button]:w-full [&_button]:min-h-11 '
                    + '[&_button]:!h-auto [&_button]:!whitespace-normal [&_button]:break-words [&_button]:!py-2',
            };
            schema.value.actions.removeClass = { ...schema.value.actions.removeClass, wrapper: 'grid-cols-12' };
            const actions = schema.value.actions.schema;
            const addSubmitAction = (key, label) => {
                actions[key] = {
                    type: 'button',
                    submits: true,
                    buttonLabel: label,
                    __baseLabel: label,
                    disabled: false,
                    addClass: {
                        button: 'btn_salva height_btn tab_label',
                        wrapper: 'after:absolute after:inset-0 after:bg-white/50 after:hidden disabled:after:block',
                    },
                    onClick: () => {
                        lastSaveAction.value = key;
                    },
                };
            };

            addSubmitAction('close', 'Salva e chiudi');

            const label = props.sheet.name === 'maintenance_degradation'
                ? 'Salva e aggiungi altro degrado'
                : 'Salva e aggiungi altro intervento';
            addSubmitAction('new', label);

            for (const key of ['continue', 'submit', 'close', 'new']) {
                if (!actions[key]) continue;
                // Keep Vueform's spinner visible until both saving and the
                // destination visit finish, while retaining the pending label.
                actions[key].loading = () => isSubmitting.value;
                actions[key].overrideClass = {
                    ...actions[key].overrideClass,
                    button_loading: 'opacity-60 cursor-wait !text-[var(--vf-color-btn)] '
                        + 'after:!static after:!ml-2 after:!mt-0 after:!align-middle after:!bg-current '
                        + 'motion-reduce:after:!animate-none',
                };
            }
        }

        const photoParagraph = props.sheet.name === 'maintenance_degradation'
            ? 'maintenance_degradation_photos'
            : 'maintenance_intervention_photos';
        const photoList = schema.value?.[photoParagraph]?.schema?.photos;
        if (photoList) photoList.max = MAINTENANCE_PHOTO_LIMIT;
        if (offlineMode) {
            photoTypeItemsLoader = configureOfflinePhotoTypeField(photoList, {
                scope,
                sheetName: props.sheet.name,
            });
        }
    };

    const initialize = async () => {
        if (!offlineMode) {
            initialized.value = true;
            return;
        }

        await photoTypeItemsLoader?.();
        const recoveredDraft = await offline.initialize();
        if (recoveredDraft?.data) {
            data.value = { ...data.value, ...recoveredDraft.data };
            clientUuid.value = recoveredDraft.clientUuid ?? clientUuid.value;
            toast.info('Bozza di manutenzione recuperata dal dispositivo.');
        }
        data.value.clientUuid = clientUuid.value;
        initialized.value = true;
    };

    const scheduleDraftSave = () => {
        if (!offlineMode || !initialized.value || !data.value) return;
        clearTimeout(draftTimer);
        draftTimer = setTimeout(() => {
            offline.saveDraft(
                JSON.parse(JSON.stringify(data.value)),
                clientUuid.value,
            ).catch(error => console.error('Unable to persist maintenance draft', error));
        }, 500);
    };

    const navigateAfterSync = (response, submitAction, modelValue = data.value, actionOverride = null) => {
        const recordId = response?.data?.data?.id;
        const action = actionOverride ?? lastSaveAction.value ?? submitAction?.value;
        if (action === 'new') {
            return visitAfterSave(buildMaintenanceCreateAgainUrl(props.sheet.name, modelValue));
        } else if (action === 'continue' && recordId) {
            return visitAfterSave(`/database/modifica-scheda/${props.sheet.name}/${recordId}?new=true`);
        } else {
            return visitAfterSave('/manutenzione');
        }
    };

    const submit = async (modelValue, submitAction, submitOptions = {}) => {
        if (isSubmitting.value) return null;
        isSubmitting.value = true;
        try {
            const saveAction = resolveMaintenanceSaveAction(
                lastSaveAction.value,
                submitOptions,
                submitAction,
            );
            const result = await offline.queue(
                JSON.parse(JSON.stringify(modelValue ?? {})),
                clientUuid.value,
                submitOptions.headers ?? {},
            );
            if (result.synced) {
                clearTimeout(draftTimer);
                toast.success('Scheda sincronizzata.');
                await navigateAfterSync(result.response, submitAction, modelValue, saveAction);
            } else if (!offline.online.value) {
                toast.info('Scheda salvata sul dispositivo. Sarà inviata al ritorno della rete.');
            } else if (result.error) {
                toast.error(offline.lastError.value?.message ?? 'Sincronizzazione non riuscita.');
            }
            return result.response ?? null;
        } finally {
            isSubmitting.value = false;
        }
    };

    const retry = async submitAction => {
        if (isSubmitting.value) return null;
        isSubmitting.value = true;
        try {
            const result = await offline?.sync();
            if (result?.synced) {
                clearTimeout(draftTimer);
                toast.success('Scheda sincronizzata.');
                await navigateAfterSync(result.response, submitAction, data.value);
            }
            return result;
        } finally {
            isSubmitting.value = false;
        }
    };

    const discard = async () => {
        if (!window.confirm('Eliminare definitivamente la bozza e tutte le fotografie locali?')) return;
        await offline?.discard();
        window.location.reload();
    };

    return {
        component: HeraMaintenanceNewSchedaExtension,
        handlesSubmit: offlineMode,
        offlineMode,
        offline,
        scope,
        draftKey,
        showCompletion,
        completionOfflineEnabled,
        statusLabel,
        transformSchema,
        extendSubmitOptions: (modelValue, submitAction, submitOptions) => {
            const action = resolveMaintenanceSaveAction(lastSaveAction.value, submitOptions, submitAction);
            const redirectTo = resolveMaintenanceSaveRedirect(props.sheet.name, action, modelValue);
            if (offlineMode) return submitOptions;

            return {
                ...submitOptions,
                saveAction: action,
                ...(redirectTo ? { redirectTo } : {}),
                ...(!editing ? {
                    headers: {
                        ...submitOptions?.headers,
                        'X-Idempotency-Key': clientUuid.value,
                    },
                } : {}),
            };
        },
        initialize,
        onDataChanged: scheduleDraftSave,
        submit,
        retry,
        discard,
        onCompleted: () => window.location.reload(),
        cleanup: () => {
            clearTimeout(draftTimer);
        },
    };
}

export const newSchedaExtensionFactories = [createHeraMaintenanceNewSchedaExtension];
